import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputBase,
  Paper,
  Tooltip
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import {green, grey, red} from '@material-ui/core/colors';
import {
  Add,
  ArrowDownward,
  Cancel,
  Check,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clear,
  Close,
  Edit,
  FilterList,
  FirstPage,
  LastPage,
  Search
} from '@material-ui/icons';
import CloseIcon from '@material-ui/icons/Close';
import MaterialTable from 'material-table';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import locale from '../locale/Localization';
import {getCompanyById} from '../script/company-service.js';

const tableIcons = {
  Filter: FilterList,
  PreviousPage: ChevronLeft,
  NextPage: ChevronRight,
  FirstPage: FirstPage,
  LastPage: LastPage,
  Add: Add,
  Edit: Edit,
  Check: Check,
  Close: Close,
  SortArrow: ArrowDownward,
  ResetSearch: Clear
};

const tableLocalization = {
  toolbar: {
    searchTooltip: locale.tableSearchTooltip,
    searchPlaceholder: locale.tableSearchPlaceholder
  },
  header: {
    actions: locale.tableHeaderActions
  },
  body: {
    emptyDataSourceMessage: locale.tableEmptyDataSourceMessage
  },
  pagination: {
    labelRowsPerPage: locale.tableLabelRowsPerPage,
    labelDisplayedRows: locale.tableLabelDisplayedRows,
    labelRowsSelect: locale.tableLabelRowsSelect,
    firstTooltip: locale.tableFirstTooltip,
    previousTooltip: locale.tablePreviousTooltip,
    nextTooltip: locale.tableNextTooltip,
    lastTooltip: locale.tableLastTooltip
  }
};

const styles = {
  paper: {marginTop: 20},
  table: {marginTop: 20},
  button: {marginLeft: 10},
  buttons: {marginLeft: 20, marginTop: 20, marginBottom: 10},
  search: {margin: '2% 20px 0px 20px'},
  progress: {margin: '30% 40%'},
  searchInputPaper: {
    marginTop: '4%',
    display: 'flex',
    alignItems: 'center',
    width: '100%'
  },
  iconButton: {padding: 10},
  divider: {height: 28, margin: 4},
  dialog: {minWidth: 580, minHeight: 550},
  dialogTitle: {paddingBottom: 0},
  dialogContent: {paddingTop: 0, marginTop: '-2%'},
  closeIcon: {
    cursor: 'pointer',
    float: 'right',
    marginTop: -5,
    marginRight: -10
  }
};

/**
 * Component class of CompanySearch.
 */
export default class CompanySearch extends Component {
  /**
   * Creates a CompanySearch.
   *
   * @param {Object} props - The property data.
   */
  constructor(props) {
    super(props);
    this.searchUidField = React.createRef();
    this.state = {
      companyId: this.props.savedSearchValue,
      companies: this.props.companyList,
      validationErrorText: '',
      loading: false,
      dialogOpen: false,
      selectedCompanyUid: null,
      selectedRow: null
    };
  }

  /**
   * Render the circular progress animation.
   *
   * @return {string} - The circular progress component.
   */
  renderCircularUnderLoad() {
    return <CircularProgress disableShrink style={styles.progress} />;
  }

  /**
   * Executes company ID search by name or UID.
   *
   */
  idSearch = () => {
    if (!this.state.loading) {
      this.setState({
        loading: true
      });
    }

    this.handleDialogClickOpen();

    if (this.state.companyId) {
      getCompanyById(this.state.companyId).then((result) => {
        if (result.status == 200) {
          const companies = result.body;
          const companiesUpdated = companies.map((item) => {
            item.companyStatus =
              item.companyStatus === 'active' ? (
                <CheckCircle style={{color: green[500]}} />
              ) : (
                <Cancel style={{color: red[500]}} />
              );
            item.town = item.address.town;
            item.address.country =
              item.address.countryIdISO2 === 'CH'
                ? 'Schweiz'
                : item.address.countryIdISO2;
            return item;
          });
          this.setState({
            companies: companiesUpdated,
            loading: false
          });
        } else if (result.status == 500) {
          this.setState({
            companies: null,
            loading: false,
            validationErrorText: locale.uidIsInvalid
          });
        } else {
          this.setState({companies: null});
        }
      });
    }
  };

  /**
   * Clear search term.
   *
   */
  clearSearchTerm = () => {
    this.setState({
      companyId: null,
      companies: null,
      validationErrorText: ''
    });
    this.searchUidField.value = null;
  };

  /**
   * Stores company's search term.
   *
   * @param {Event} event - The fired event.
   */
  storeCompanyID = (event) => {
    this.setState({
      companyId: event.target.value
    });

    if (event.target.value) {
      this.setState({
        validationErrorText: null
      });
    }
  };

  /**
   * Function to save the selected row data.
   *
   * @param {Object} selectedRow - The data of the selected row.
   */
  saveSelectedRow = (selectedRow: Object) => {
    this.setState({selectedRow});
  };

  /**
   * Renders the result table.
   *
   * @return {string} - The form to be rendered.
   */
  renderResults = () => {
    return (
      <div>
        {this.state.companies && (
          <MaterialTable
            style={styles.table}
            icons={tableIcons}
            localization={tableLocalization}
            title={locale.searchResult}
            columns={[
              {title: locale.uid, field: 'uid'},
              {title: locale.name, field: 'name'},
              {title: locale.status, field: 'companyStatus'},
              {title: locale.location, field: 'town'}
            ]}
            data={this.state.companies}
            options={{
              sorting: true,
              showTitle: true,
              search: false,
              rowStyle: (rowData) => ({
                backgroundColor:
                  this.state.selectedRow &&
                  this.state.selectedRow.tableData.id === rowData.tableData.id
                    ? grey[300]
                    : '#FFF'
              })
            }}
            onRowClick={(event, selectedRow) =>
              this.saveSelectedRow(selectedRow)
            }
          />
        )}
      </div>
    );
  };

  /**
   * Handle dialog open.
   *
   */
  handleDialogClickOpen = () => {
    this.setState({dialogOpen: true});
  };

  /**
   * Handle dialog close.
   *
   */
  handleDialogClickClose = () => {
    this.setState({dialogOpen: false});
  };

  /**
   * Handle search input change.
   *
   * @param {Event} event - The fired event.
   */
  handleInputChange = (event) => {
    this.storeCompanyID(event);
  };

  /**
   * Handle search submit.
   *
   */
  handleSearchSubmit = () => {
    this.idSearch();
  };

  /**
   * Handle search cancel.
   *
   */
  handleSearchCancel = () => {
    this.setState({selectedRow: null});
    this.handleDialogClickClose();
  };

  /**
   * Handle dialog submit.
   *
   */
  handleDialogSubmit = () => {
    this.props.submitHandler(this.state.selectedRow);
    this.handleDialogClickClose();
  };

  /**
   * Sub-render method for the dialog.
   *
   * @return {string} - The component contents.
   */
  renderDialog = () => {
    return (
      <Dialog
        disableBackdropClick
        open={this.state.dialogOpen}
        onClose={() => this.handleDialogClickClose()}
        aria-labelledby='dialog-title'
        maxWidth={'md'}
      >
        <DialogTitle id='dialog-title' style={styles.dialogTitle}>
          {locale.companySearch}
          <IconButton
            edge='start'
            color='default'
            style={styles.closeIcon}
            onClick={() => this.handleSearchCancel()}
            aria-label='close'
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={styles.dialogContent}>
          {this.state.loading && this.renderCircularUnderLoad()}
          {!this.state.loading && this.renderResults()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => this.handleSearchCancel()}
            color='primary'
            variant='outlined'
          >
            {locale.cancelButton}
          </Button>
          <Button
            disabled={!this.state.selectedRow}
            type='submit'
            color='primary'
            variant='contained'
            onClick={() => this.handleDialogSubmit()}
          >
            {locale.applyButton}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  /**
   * Renders the CompanySearch component.
   *
   * @return {string} - The company search component to be rendered.
   */
  render() {
    return (
      <div id='uid-search-input'>
        <Paper style={styles.searchInputPaper}>
          <InputBase
            fullWidth
            margin='dense'
            variant='outlined'
            style={styles.search}
            id='company-uid-input'
            name='companyId'
            placeholder={locale.companyNameOrUid}
            defaultValue={this.state.companyId}
            onChange={(event) => this.handleInputChange(event)}
            inputRef={(el) => (this.searchUidField = el)}
          />
          <Tooltip
            title={this.state.companyId ? locale.clearSearch : ''}
            placement='top'
          >
            <div>
              <IconButton
                disabled={!this.state.companyId}
                style={styles.iconButton}
                aria-label='clear'
                onClick={() => this.clearSearchTerm()}
              >
                <Clear />
              </IconButton>
            </div>
          </Tooltip>
          <Divider style={styles.divider} orientation='vertical' />
          <Tooltip
            title={
              this.state.companyId
                ? locale.startSearch
                : locale.emptySearchValue
            }
            placement='top'
          >
            <div>
              <IconButton
                disabled={!this.state.companyId}
                color='primary'
                style={styles.iconButton}
                aria-label='search'
                onClick={() => this.handleSearchSubmit()}
              >
                <Search />
              </IconButton>
            </div>
          </Tooltip>
        </Paper>
        {this.renderDialog()}
      </div>
    );
  }
}

CompanySearch.propTypes = {
  submitHandler: PropTypes.func.isRequired,
  onCompanyDetailsShown: PropTypes.func,
  companyList: PropTypes.array,
  savedSearchValue: PropTypes.string
};
